import React, { useState, useEffect } from 'react';
import { confirm } from '@/lib/confirm';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import { Modal } from '@/components/custom/modal/modal';
import { useSession } from '@/hooks/useSession';
import { TeacherDashboardProvider } from './providers/TeacherDashboardProvider';
import { DashboardMetrics } from './dashboard-metrics';
import Survey from '@/components/custom/survey';
import { useSurvey } from '@/hooks/useSurvey';
import { DashboardStudent } from './dashboard-student';
import Onboarding from '@/components/custom/onboarding';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import MockQuestionBookCard from './MockQuestionBookCard';
import MockQuestionBook from './MockQuestionBook';
import { DashboardTeacher } from './dashboard-teacher';
import { DashboardContent } from '@/layouts/dashboard';
import { PageContent, Page, PageSidebar } from '@/components/custom/page';
import { EventsCalendar } from '@/components/custom/dashboard';
import EventForm from '@/components/event-form/event-form';
import LiveClassForm from '@/pages/LiveClasses/LiveClassForm';
import SupportPopupModal from '@/components/custom/support-popup-modal';

export function Dashboard(props) {
  return (
    <TeacherDashboardProvider>
      <ScreenContentDashboard {...props} />
    </TeacherDashboardProvider>
  );
}

function ScreenContentDashboard(props) {
  const { t } = useTranslation();
  const client = useClient();
  const [slots, setSlots] = useState([]);
  const [editingEvent, setEditingEvent] = useState();
  const [events, setEvents] = useState([]);
  const [eventDates, setEventDates] = useState([]);
  const [showSurvey, setShowSurvey] = useState(false);
  const [showFormEvent, setShowFormEvent] = useState(false);
  const [showLiveClassForm, setShowLiveClassForm] = useState(false);
  const [editingLiveClass, setEditingLiveClass] = useState(false);
  const [currentLive, setCurrentLive] = useState({});
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const { getStudentSurvey, currentStudentSurvey } = useSurvey();
  const { session } = useSession();
  const profiles = session?.user?.profileNames || (session?.user && session?.user['profile-names']) || [];
  const path = props.path;
  const [showMockQuestionBook, setShowMockQuestionBook] = useState(session?.user?.userDetail?.showQuestionBookBanner);
  const [mockQuestionBook, setmockQuestionBook] = useState({});
  const [mockLoading, setMockLoading] = useState(true);
  const [visibleEvents, setVisibleEvents] = useState(2);

  // Alertas (desafio/prova prestes a expirar) vêm no mesmo endpoint de eventos/aulas
  // ao vivo, mas não têm data própria no calendário: aparecem sempre no dia atual
  // (não no dia do prazo) e por último, depois de eventos e aulas ao vivo — e não
  // marcam o dia no calendário.
  const isSelectedDateToday = moment(selectedDate).isSame(moment(), 'day');

  const eventsToday = [
    ...(events || []).filter(i => i?.type !== 'alert' && moment(i?.['initial-date']).format('YYYY-MM-DD') === moment(selectedDate).format('YYYY-MM-DD')),
    ...(isSelectedDateToday ? (events || []).filter(i => i?.type === 'alert') : [])
  ];
  const authorityLevel = useAuthorityLevel(session);
  const hasMoreEvents = visibleEvents < eventsToday.length;

  const fetchSlots = async () => {
    try {
      const today = moment().format('YYYY-MM-DD');
      const { data } = await client.fetch(`slots?filter[date_between][initial]=${today}&filter[date_between][final]=${today}`);
      setSlots(data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchEvents = async initDate => {
    try {
      const initialDate = moment(initDate).startOf('month').format('YYYY-MM-DD');
      const endDate = moment(initDate).endOf('month').format('YYYY-MM-DD');

      const response = await client.request(`/events?filter[date_between][initial]=${initialDate}&filter[date_between][final]=${endDate}`);
      setEvents((response.data || []).map(({ id, type, attributes }) => ({ id, type, ...attributes })));
    } catch (e) {
      console.error(e);
    }
  };

  const confirmDeleteEvent = async event => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteEvent'),
      description: t('projects.warningDeleteCannotBeUndoneText'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteEvent(event);
    }
  };

  const deleteEvent = async event => {
    const { error } = await client.delete(['events', event.id]);
    if (!error) {
      toast.success(t('toast.successDeleteEvent'));
      setEvents(prevState => prevState.filter(item => item.id !== event.id));
    }
  };

  const editEvent = event => {
    setEditingEvent(event);
    setShowFormEvent(true);
  };

  const closeFormEvent = () => {
    setShowFormEvent(false);
    setEditingEvent(null);
  };

  const closeLiveClassForm = () => {
    setShowLiveClassForm(false);
    setEditingLiveClass(false);
    setCurrentLive({});
  };

  const setLiveClassrooms = valueOrUpdater => {
    setEvents(prevState => {
      const nextEvents = typeof valueOrUpdater === 'function' ? valueOrUpdater(prevState) : valueOrUpdater;
      return (nextEvents || []).map(event => (event.type ? event : { ...event, type: 'live-classroom' }));
    });
  };

  const onCloseSurvey = () => {
    setShowSurvey(false);
    getStudentSurvey();
  };

  const onActiveStartDateChange = ({ activeStartDate }) => {
    setSelectedDate(activeStartDate);
    fetchEvents(activeStartDate);
  };

  const getmockQuestionBook = async () => {
    setMockLoading(true);
    try {
      const { data, error } = await client.fetch('question_books/next');
      if (error) {
        toast.error(t('toast.errorGetSimulations'));
      } else {
        setmockQuestionBook(data);
        setMockLoading(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!profiles.includes('Professor')) {
      fetchSlots();
    }
    fetchEvents(selectedDate);
    getStudentSurvey();
    if (authorityLevel === 'student') {
      getmockQuestionBook();
    }
  }, []);

  useEffect(() => {
    // Alertas não marcam dia no calendário — só eventos e aulas ao vivo têm data própria.
    setEventDates((events || []).filter(i => i?.type !== 'alert').map(i => moment(i['initial-date']).format('YYYY-MM-DD')));
  }, [events]);

  useEffect(() => {
    const profiles = session?.user?.profileNames || (session?.user && session?.user['profile-names']) || [];
    const newValue = mockQuestionBook && !mockQuestionBook['is-question-book-done'] && session?.user?.userDetail?.showQuestionBookBanner && !mockLoading && !profiles.includes('Administrador');
    setShowMockQuestionBook(newValue);
  }, [mockLoading, mockQuestionBook, session]);

  return (
    <DashboardContent>
      <Page>
        <PageContent className="gap-0">{profiles.includes('Administrador') ? <DashboardMetrics /> : profiles.includes('Aluno') ? <DashboardStudent /> : <DashboardTeacher />}</PageContent>

        <PageSidebar
          variant="calendar"
          overflow={false}
        >
          {showMockQuestionBook && profiles.includes('Aluno') && <MockQuestionBookCard mockQuestionBook={mockQuestionBook} />}

          <EventsCalendar
            eventsToday={eventsToday}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            onActiveStartDateChange={onActiveStartDateChange}
            eventDates={eventDates}
            visibleEvents={visibleEvents}
            setVisibleEvents={setVisibleEvents}
            hasMoreEvents={hasMoreEvents}
            editEvent={editEvent}
            confirmDeleteEvent={confirmDeleteEvent}
            setShowFormEvent={setShowFormEvent}
            setEditingLiveClass={setEditingLiveClass}
            setShowLiveClassForm={setShowLiveClassForm}
            setCurrentLive={setCurrentLive}
            setLiveClassrooms={setLiveClassrooms}
          />
        </PageSidebar>
      </Page>

      <Modal
        show={showFormEvent}
        onClose={closeFormEvent}
      >
        <EventForm
          onClose={() => setShowFormEvent(false)}
          editingEvent={editingEvent}
          events={events}
          setEvents={setEvents}
        />
      </Modal>

      <LiveClassForm
        show={showLiveClassForm}
        onClose={closeLiveClassForm}
        editingLiveClass={editingLiveClass}
        currentLive={currentLive}
        weekDays={events}
        setWeekDays={setLiveClassrooms}
      />

      <Modal
        show={showSurvey}
        onClose={() => setShowSurvey(false)}
        contentClassName="modal__content--p-md"
      >
        {currentStudentSurvey && (
          <Survey
            studentSurvey={currentStudentSurvey}
            onClose={onCloseSurvey}
          />
        )}
      </Modal>

      {mockQuestionBook && showMockQuestionBook && !mockLoading && profiles.includes('Aluno') && (
        <MockQuestionBook
          setShowMockQuestionBook={setShowMockQuestionBook}
          showMockQuestionBook={showMockQuestionBook}
          mockQuestionBook={mockQuestionBook}
        />
      )}

      <Onboarding
        path={path}
        show={showOnboarding}
        setShowOnboarding={setShowOnboarding}
        onClose={() => setShowOnboarding(false)}
        profiles={profiles}
        title={t('onboarding.titleDashboard')}
        videoId="oO3DxD_nqws"
      />

      <SupportPopupModal />
    </DashboardContent>
  );
}
